import {
  Checkbox,
  IconButton,
  ListMenu,
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@compass/core";

import EventNoteIcon from "@mui/icons-material/EventNote";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import { ListBeProperty } from "components";
import {
  LkRoleId,
  Permission,
  PermissionAction,
  SortObject,
  StudentListDto,
  StudentListResource,
  TenantInfo,
} from "generated/openApi";
import { studentServicePageRoute } from "pages/students/student-services/StudentServicePage/StudentServicePage";
import { useStudentListMenuItems } from "pages/students/students/StudentListPage/useStudentListMenuItems";
import { studentPageRoute } from "pages/students/students/StudentPage/StudentPage";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "redux/store";
import { isAuthorized } from "services";

export const DeletedStudentTable = (props: {
  rootPermission: Permission;
  tenantTextingInfo: TenantInfo | undefined;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  students: StudentListDto[];
  listTotal: number | undefined;
  resource: StudentListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<any>;
  loadList: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];
  const menuItems = useStudentListMenuItems({ tenantTextingInfo: props.tenantTextingInfo, permission: props.rootPermission, selectedIds: props.selectedIds });
  const roleId = useSelector((state: RootState) => state.app.roleId);

  const view = (item: StudentListDto) => {
    if (roleId === LkRoleId.DataEntry) return undefined;
    return () => navigate(studentPageRoute.getPath({ id: item.id, crudMode: "view" }));
  };

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {menuItems.length > 0 && (
              <TableCell type="select">
                <ListMenu
                  selectedIds={props.selectedIds}
                  setSelectedIds={props.setSelectedIds}
                  listTotal={props.listTotal}
                  fetchFunction={() => props.getIdsPromise()}
                  actionMenuItems={menuItems}
                />
              </TableCell>
            )}

            {props.resource.customIdTypes?.map((customIdType) => (
              <TableCell key={customIdType.id}>
                <ListBeProperty
                  listProperty={properties?.[`customId${customIdType.id}}`]}
                  displayName={customIdType.name}
                  sortObjects={props.sortObjects}
                  onChange={(sortObjects) => props.sort(sortObjects)}
                  searchFocused={props.searchFocused}
                />
              </TableCell>
            ))}

            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentListDto>("fullName"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentListDto>("currentSchool"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell align="center">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentListDto>("currentGradeLevel"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.students.map((studentItem) => (
            <TableRow key={studentItem.id} onClick={view(studentItem)}>
              {menuItems.length > 0 && (
                <TableCell type="select">
                  <Checkbox
                    checked={props.selectedIds.includes(studentItem.id)}
                    onClick={(e) => props.selectId(studentItem.id)}
                  />
                </TableCell>
              )}

              {props.resource.customIdTypes?.map((customIdType) => (
                <TableCell key={customIdType.id}>
                  <Typography
                    color={
                      customIdType.id === 1 && studentItem[`customId${customIdType.id}`]?.startsWith("X-")
                        ? "error"
                        : undefined
                    }
                    variant="inherit"
                  >
                    {studentItem[`customId${customIdType.id}`]}
                  </Typography>
                </TableCell>
              ))}

              <TableCell>
                {studentItem.fullName}{" "}
                {studentItem.middleName &&
                  studentItem.middleName.length > 0 &&
                  studentItem.middleName.charAt(0).toUpperCase() + "."}
              </TableCell>
              <TableCell>{studentItem.currentSchool}</TableCell>
              <TableCell align="center">{studentItem.currentGradeLevel}</TableCell>
              <TableCell type="action">
                {isAuthorized(Permission.StudentService, PermissionAction.Create) && (
                  <IconButton
                    onClick={() =>
                      navigate({
                        pathname: studentServicePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                        search: studentServicePageRoute.getOptionalParams?.({
                          hideStudent: true,
                          studentId: studentItem.id,
                        }),
                      })
                    }
                    color="primary"
                    size="small"
                  >
                    <Stack direction="row" alignItems="center">
                      <Typography variant="caption">+</Typography>
                      <EventNoteIcon />
                    </Stack>
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
